import React from 'react';
import { QualityReport as QualityReportType, QualityConfig } from '../utils/dataQuality';
import { AlertTriangle, CheckCircle, Info, Settings } from 'lucide-react';

interface QualityReportProps {
  report: QualityReportType;
  config: QualityConfig;
  onConfigChange?: (config: Partial<QualityConfig>) => void;
}

export const QualityReport: React.FC<QualityReportProps> = ({ 
  report, 
  config, 
  onConfigChange 
}) => {
  const getQualityColor = (score: number) => {
    if (score >= 90) return 'text-green-600 bg-green-50';
    if (score >= 70) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const getQualityIcon = (score: number) => {
    if (score >= 90) return <CheckCircle className="w-5 h-5 text-green-600" />;
    if (score >= 70) return <Info className="w-5 h-5 text-yellow-600" />;
    return <AlertTriangle className="w-5 h-5 text-red-600" />;
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 mb-6" dir="rtl">
      <div className="flex items-center gap-3 mb-6">
        <Settings className="w-6 h-6 text-blue-600" />
        <h2 className="text-xl font-bold text-gray-900">تقرير جودة البيانات</h2>
      </div>

      {/* نقاط الجودة الإجمالية */}
      <div className={`rounded-lg p-4 mb-6 ${getQualityColor(report.qualityScore)}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {getQualityIcon(report.qualityScore)}
            <div>
              <h3 className="font-bold text-lg">نقاط الجودة الإجمالية</h3>
              <p className="text-sm opacity-80">
                {report.qualityScore >= 90 && 'ممتاز - البيانات عالية الجودة'}
                {report.qualityScore >= 70 && report.qualityScore < 90 && 'جيد - البيانات مقبولة مع بعض التحسينات'}
                {report.qualityScore < 70 && 'يحتاج تحسين - البيانات تحتاج مراجعة'}
              </p>
            </div>
          </div>
          <div className="text-3xl font-bold">
            {report.qualityScore}%
          </div>
        </div>
      </div>

      {/* إحصائيات البيانات */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-blue-50 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-blue-600">{report.totalResponses}</div>
          <div className="text-sm text-blue-800">إجمالي الاستجابات</div>
        </div>
        
        <div className="bg-green-50 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-green-600">{report.validResponses}</div>
          <div className="text-sm text-green-800">الاستجابات الصالحة</div>
        </div>
        
        <div className="bg-yellow-50 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-yellow-600">{report.zeroValues}</div>
          <div className="text-sm text-yellow-800">القيم الصفرية المزالة</div>
        </div>
        
        <div className="bg-red-50 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-red-600">{report.outliers.length}</div>
          <div className="text-sm text-red-800">القيم الشاذة المكتشفة</div>
        </div>
      </div>

      {/* إعدادات التنظيف المطبقة */}
      <div className="bg-gray-50 rounded-lg p-4 mb-6">
        <h3 className="font-bold text-gray-900 mb-3">الإعدادات المطبقة تلقائياً</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <div className="flex items-center gap-2">
            <div className={`w-3 h-3 rounded-full ${config.ignoreZeroValues ? 'bg-green-500' : 'bg-gray-300'}`}></div>
            <span>تجاهل القيم الصفرية: {config.ignoreZeroValues ? 'مفعل' : 'معطل'}</span>
          </div>
          
          <div className="flex items-center gap-2">
            <div className={`w-3 h-3 rounded-full ${config.removeOutliers ? 'bg-green-500' : 'bg-gray-300'}`}></div>
            <span>إزالة القيم الشاذة: {config.removeOutliers ? 'مفعل' : 'معطل'}</span>
          </div>
          
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-blue-500"></div>
            <span>مستوى الثقة: {(config.confidenceLevel * 100).toFixed(0)}%</span>
          </div>
          
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-purple-500"></div>
            <span>الحد الأدنى للعينة: {config.minResponseThreshold}</span>
          </div>
        </div>
      </div>

      {/* التوصيات */}
      {report.recommendations.length > 0 && (
        <div className="border-t pt-6">
          <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Info className="w-5 h-5 text-blue-600" />
            التوصيات والملاحظات
          </h3>
          <div className="space-y-3">
            {report.recommendations.map((recommendation, index) => (
              <div 
                key={index}
                className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg"
              >
                <div className="w-2 h-2 rounded-full bg-blue-600 mt-2 flex-shrink-0"></div>
                <p className="text-blue-800 text-sm leading-relaxed">{recommendation}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ملاحظة تقنية */}
      <div className="mt-6 p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border border-indigo-200">
        <div className="flex items-start gap-3">
          <Settings className="w-5 h-5 text-indigo-600 mt-0.5" />
          <div>
            <h4 className="font-bold text-indigo-900 mb-1">ملاحظة تقنية</h4>
            <p className="text-indigo-700 text-sm leading-relaxed">
              تم تطبيق هذه الإعدادات تلقائياً بناءً على تحليل ذكي لبيانات الاستبيان. 
              يمكن للنظام اكتشاف أنماط البيانات وتطبيق أفضل الممارسات في التحليل الإحصائي 
              لضمان الحصول على أدق النتائج وأكثرها موثوقية.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}; 